import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { Injectable, Inject, Component } from '@angular/core';

import { Broker } from '@c4/core';
import { Store } from '@ngrx/store';

import { ProgrammingEventService } from '../../common/services/event.service';
import { CodeItemService } from '../../common/services/codeitem.service';
import { SharedProgrammingService } from '../../common/services/shared-programming.service';

export interface ProgrammingEvent {
  category?: string;
  device?: any;
  devices?: Array<any>;
  event?: any;
  events?: Array<any>;
  deviceSearchText: string;
}

export interface ProgrammingCodeItem {
  parentCodeItemId?: number;
  type?: number;
  category?: any;
  devices?: Array<any>;
  device?: any;
  commands?: Array<any>;
  command?: any;
  conditions?: Array<any>;
  condition?: any;
  loops?: Array<any>;
  loop?: any;
}

export interface ProgrammingContext {
  event: ProgrammingEvent;
  codeitem: ProgrammingCodeItem;

  ready: boolean;
  error: boolean;
  errors?: Array<any>;
};

const INITIAL_STATE: ProgrammingContext = {
  ready: false,
  event: {
    deviceSearchText: ''
  },
  codeitem: {},
  error: false
};

const STORE_NAME: string = 'PROGRAMMING:ADVANCED:';

export const SET_WORKING: string = STORE_NAME + 'SET_WORKING';
export const SET_ERRORS: string = STORE_NAME + 'SET_ERRORS';
export const CLEAR_ERRORS: string = STORE_NAME + 'CLEAR_ERRORS';

export const SELECT_EVENT_CATEGORY: string = STORE_NAME + 'SELECT_EVENT_CATEGORY';
export const CLEAR_EVENT_CATEGORY: string = STORE_NAME + 'CLEAR_EVENT_CATEGORY';
export const SET_EVENT_DEVICES: string = STORE_NAME + 'SET_EVENT_DEVICES';
export const SELECT_EVENT_DEVICE: string = STORE_NAME + 'SELECT_EVENT_DEVICE';
export const CLEAR_EVENT_DEVICE: string = STORE_NAME + 'CLEAR_EVENT_DEVICE';
export const SET_EVENTS: string = STORE_NAME + 'SET_EVENTS';
export const SELECT_EVENT: string = STORE_NAME + 'SELECT_EVENT';
export const CLEAR_EVENT: string = STORE_NAME + 'CLEAR_EVENT';
export const SET_EVENT_DEVICE_SEARCH: string = STORE_NAME + 'SET_EVENT_DEVICE_SEARCH';

export const SET_CODEITEM_INITIAL_STATE: string = STORE_NAME + 'SET_CODEITEM_INITIAL_STATE';
export const SELECT_CODEITEM_CATEGORY: string = STORE_NAME + 'SELECT_CODEITEM_CATEGORY';
export const CLEAR_CODEITEM_CATEGORY: string = STORE_NAME + 'CLEAR_CODEITEM_CATEGORY';
export const SET_CODEITEM_DEVICES: string = STORE_NAME + 'SET_CODEITEM_DEVICES';
export const SELECT_CODEITEM_DEVICE: string = STORE_NAME + 'SELECT_CODEITEM_DEVICE';
export const CLEAR_CODEITEM_DEVICE: string = STORE_NAME + 'CLEAR_CODEITEM_DEVICE';
export const SET_CODEITEM_COMMANDS: string = STORE_NAME + 'SET_CODEITEM_COMMANDS';
export const SELECT_COMMAND: string = STORE_NAME + 'SELECT_COMMAND';
export const CLEAR_COMMAND: string = STORE_NAME + 'CLEAR_COMMAND';
export const SET_CONDITIONS: string = STORE_NAME + 'SET_CODEITEM_CONDITIONS';
export const SELECT_CONDITION: string = STORE_NAME + 'SELECT_CONDITION';
export const CLEAR_CONDITON: string = STORE_NAME + 'CLEAR_CONDITION';
export const SET_LOOPS: string = STORE_NAME + 'SET_CODEITEM_LOOPS';
export const SELECT_LOOP: string = STORE_NAME + 'SELECT_LOOP';
export const CLEAR_LOOP: string = STORE_NAME + 'CLEAR_LOOP';
export const SET_CODEITEM_DONE: string = STORE_NAME + 'SET_CODEITEM_DONE';
export const CLEAR_CODEITEM: string = STORE_NAME + 'SET_CODEITEM_DONE';
export const SET_COMPOSED_DISPLAY: string = STORE_NAME + 'SET_COMPOSED_DISPLAY';
export const SET_CODEITEM_PARENT: string = STORE_NAME + 'SET_CODEITEM_PARENT';
export const SET_CODEITEM_COMPLETE: string = STORE_NAME + 'SET_CODEITEM_COMPLETE';

export function advancedReducer(state: ProgrammingContext = INITIAL_STATE, {type, payload}) {
  switch (type) {
    // general purpose
    case SET_WORKING: return Object.assign({}, state, { working: payload });
    case SET_ERRORS: return Object.assign({}, state, { errors: payload, error: (payload.length > 0) });
    case CLEAR_ERRORS: return Object.assign({}, state, { errors: { error: false, errors: undefined } });

    // for event selection
    case SELECT_EVENT_CATEGORY: return Object.assign({}, state, { event: Object.assign({}, state, { category: payload }) });
    case SET_EVENT_DEVICES: return Object.assign({}, state, { event: Object.assign({}, state, { devices: payload }) });
    case SELECT_EVENT_DEVICE: return Object.assign({}, state, { event: Object.assign({}, state, { device: payload }) });
    case SET_EVENTS: return Object.assign({}, state, { event: Object.assign({}, state, { events: payload }) });
    case SELECT_EVENT: return Object.assign({}, state, { event: Object.assign({}, state, { event: payload }) });

    // for adding code items
    case SELECT_CODEITEM_CATEGORY: return Object.assign({}, state, { codeitem: Object.assign({}, state, { category: payload }) });
    case SET_CODEITEM_DEVICES: return Object.assign({}, state, { codeitem: Object.assign({}, state, { devices: payload }) });
    case SELECT_CODEITEM_DEVICE: return Object.assign({}, state, { codeitem: Object.assign({}, state, { device: payload }) });
    case SET_CODEITEM_COMMANDS: return Object.assign({}, state, { codeitem: Object.assign({}, state, { commands: payload }) });
    case SELECT_COMMAND: return Object.assign({}, state, { codeitem: Object.assign({}, state, { command: payload }) });
    case SET_CONDITIONS: return Object.assign({}, state, { codeitem: Object.assign({}, state, { conditions: payload }) });
    case SELECT_CONDITION: return Object.assign({}, state, { codeitem: Object.assign({}, state, { condition: payload }) });
    case SET_LOOPS: return Object.assign({}, state, { codeitem: Object.assign({}, state, { loops: payload }) });
    case SELECT_LOOP: return Object.assign({}, state, { codeitem: Object.assign({}, state, { loop: payload }) });
    case SET_CODEITEM_INITIAL_STATE: return Object.assign({}, state, { codeitem: payload });

    default: state;
  }
};

@Injectable()
export class AdvancedProgrammingService {

  constructor(
    private broker: Broker,
    private store: Store<any>,
    private shared: SharedProgrammingService
  ) { }

  init() {
    this.broker.connected.subscribe(connected => {
      if (connected) {
        // TODO ??
      }
    });
  }

}



// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/advanced/services/advanced-programming.service.ts